import clsx from "clsx";
import type React from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { getDisabledReasonTooltip } from "@/utils/paymentMethodStatus";

import styles from "./OfflineTooltip.module.css";
import type { TooltipProps } from "./types";

import type { EnumDisabledReason } from "@/api/cashier";

type OfflineTooltipProps = {
  /** Whether the wrapped element is offline and the tooltip should be shown. */
  offline?: boolean;
  /** Reason the payment method is disabled, used to pick the tooltip message. */
  disabledReason?: EnumDisabledReason;
  children: ReactNode;
} & Partial<Omit<TooltipProps, "content" | "children">>;

export function withOfflineTooltip(
  WrappedComponent: React.ComponentType<TooltipProps>
) {
  return function OfflineTooltipWrapper({
    offline,
    disabledReason,
    children,
    className,
    usePortal,
    ...props
  }: OfflineTooltipProps) {
    const { t } = useTranslation();
    const content = offline
      ? getDisabledReasonTooltip(t, disabledReason)
      : undefined;

    if (!content) {
      return <>{children}</>;
    }
    return (
      <WrappedComponent
        position="right"
        {...props}
        usePortal={usePortal}
        content={content}
        className={clsx(
          styles.offlineTooltip,
          usePortal && styles.portal,
          className
        )}
      >
        {children}
      </WrappedComponent>
    );
  };
}
